import React, { forwardRef, useContext, useState } from 'react'
import './index.scss'
import { ICONS } from '@Const/common'
import { TableHeader, TableRowData } from '@Components/ViewWork/Table'
import { z } from 'zod'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import { Maybe } from '@Types'
import TablesForm from '@Components/ViewWork/Table/TablesForm'
import OverclaimMemberStatusPill from '@Components/StatusPills/OverclaimMemberStatusPill'
import { isOverclaimClosed } from '@Entities/overclaim/overclaim-status'
import dayjs from 'dayjs'
import { RejectionReason } from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/OverclaimRejectionReason'
import { rejectionReasonContent } from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/OverclaimRejectionReasonContent'

export interface GroupedTableInfo {
    groupedBy?: Maybe<string>
    groupedValue?: string | number
    memberStatus?: Maybe<string>
    statusReason?: Maybe<RejectionReason>
    documentationDueDate?: Maybe<string>
    headerRow?: TableHeader
    rows: TableRowData[]
}

interface GroupedTableProps {
    tableInfo: GroupedTableInfo[]
    icon: string
    validationSchema?: z.Schema<any, any>
    rowsAreValid(isValid: boolean): void
}

const GroupedTable = forwardRef<HTMLDivElement, GroupedTableProps>((props: GroupedTableProps, ref) => {
    const { tableInfo, icon, validationSchema, rowsAreValid } = props
    const { rightsholderStore, overclaimViewStore } = useContext<RootStore>(StoresContext)
    const isCurrentRightsholder = rightsholderStore.memberSummary.name === tableInfo[0].groupedBy
    const overclaimIsClosed = isOverclaimClosed(overclaimViewStore.overclaimViewItem?.overclaimStatusFilter)

    const [isExpanded, setIsExpanded] = useState<boolean>(isCurrentRightsholder)

    const isNumber = typeof tableInfo[0].groupedValue === 'number'

    const { memberStatus, statusReason, documentationDueDate } = tableInfo[0]
    const showDueDateBanner = !overclaimIsClosed && !!documentationDueDate && memberStatus !== 'ACCEPTED'

    return (
        <>
            <div className="grouped-table-header" ref={isCurrentRightsholder ? ref : undefined}>
                <div className="display left">
                    <img className="icon" src={icon} />
                    <p className="grouped-name">{tableInfo[0].groupedBy}</p>
                    {isCurrentRightsholder && <p className="owner-indicator"> - YOU</p>}
                    {!overclaimIsClosed && memberStatus && <OverclaimMemberStatusPill status={memberStatus} />}
                </div>
                <div className="display right">
                    {isNumber ? Number(tableInfo[0].groupedValue).toFixed(2) + '%' : tableInfo[0].groupedValue}
                    <div
                        className="expand-publisher"
                        onClick={() => {
                            setIsExpanded(!isExpanded)
                        }}
                    >
                        <img
                            src={isExpanded ? ICONS.chevron_drop_up : ICONS.chevron_drop_down}
                            alt="expand-cell-toggle"
                        />
                    </div>
                </div>
            </div>
            {isCurrentRightsholder && statusReason && memberStatus === 'REJECTED' && (
                <div className="status-reason">
                    <p className="status-reason-header">Rejection Reason</p>
                    <div className="status-reason-text">{rejectionReasonContent[statusReason]}</div>
                </div>
            )}
            {showDueDateBanner && (
                <div className="due-date-container">
                    <img className="icon" src={ICONS.clock} alt="due-date" />
                    <p>Due {dayjs(documentationDueDate).format('MMM D, YYYY')}</p>
                </div>
            )}

            {isExpanded && (
                <TablesForm
                    className="publishers-table"
                    tableInfos={tableInfo}
                    validationSchema={validationSchema}
                    rowsAreValid={(isValid: boolean) => isCurrentRightsholder && rowsAreValid(isValid)}
                    hideAddButton
                />
            )}
        </>
    )
})

GroupedTable.displayName = 'GroupedTable'
export default GroupedTable
